import { useState, useEffect } from 'react';

const Permaculture = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('permaculture');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const permacultureZones = [
    {
      zone: 'Zone 1',
      title: 'Kitchen Garden',
      description: 'Herbs and vegetables closest to the home for daily use',
      image: 'https://readdy.ai/api/search-image?query=Kitchen%20garden%20with%20herbs%20and%20vegetables%20near%20Indian%20home%2C%20dense%20mixed%20plantation%20of%20culinary%20herbs%2C%20layered%20vegetable%20garden%2C%20permaculture%20zone%20one%20design%2C%20home%20gardening%2C%20fresh%20herbs%20and%20greens&width=400&height=300&seq=zone-1&orientation=landscape',
      icon: 'ri-home-garden-line'
    },
    {
      zone: 'Zone 2',
      title: 'Food Forest',
      description: 'Dense mixed plantation with fruit trees and understory crops',
      image: 'https://readdy.ai/api/search-image?query=Food%20forest%20with%20mixed%20fruit%20trees%20and%20understory%20plants%2C%20layered%20permaculture%20system%2C%20dense%20plantation%20with%20multiple%20crop%20levels%2C%20sustainable%20forest%20gardening%2C%20natural%20ecosystem%20design%2C%20biodiversity%20in%20agriculture&width=400&height=300&seq=food-forest&orientation=landscape',
      icon: 'ri-tree-line'
    },
    {
      zone: 'Zone 3',
      title: 'Native Species',
      description: 'Indigenous plants including pepper and traditional crops',
      image: 'https://readdy.ai/api/search-image?query=Native%20Indian%20pepper%20plants%20and%20indigenous%20crops%2C%20traditional%20spice%20cultivation%2C%20native%20species%20preservation%2C%20wild%20pepper%20vines%2C%20indigenous%20agricultural%20biodiversity%2C%20natural%20habitat%20restoration&width=400&height=300&seq=native-species&orientation=landscape',
      icon: 'ri-leaf-line'
    },
    {
      zone: 'Zone 4',
      title: 'Wild Areas',
      description: 'Natural habitats where nature sustains itself',
      image: 'https://readdy.ai/api/search-image?query=Wild%20natural%20habitat%20in%20Indian%20countryside%2C%20untouched%20natural%20ecosystem%2C%20native%20wildlife%20habitat%2C%20natural%20forest%20area%2C%20biodiversity%20conservation%2C%20wild%20plants%20and%20natural%20succession&width=400&height=300&seq=wild-areas&orientation=landscape',
      icon: 'ri-plant-line'
    }
  ];

  const principles = [
    {
      title: 'Plant Memory',
      description: 'Understanding how plants remember and adapt to their environment',
      icon: 'ri-brain-line',
      color: 'text-green-600'
    },
    {
      title: 'Natural Cycles',
      description: 'Working with seasonal patterns and natural rhythms',
      icon: 'ri-refresh-line',
      color: 'text-blue-600'
    },
    {
      title: 'Soil Building',
      description: 'Creating rich, living soil through natural processes',
      icon: 'ri-seedling-line',
      color: 'text-brown-600'
    },
    {
      title: 'Water Wisdom',
      description: 'Efficient water use and natural water cycles',
      icon: 'ri-drop-line',
      color: 'text-cyan-600'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-green-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Permaculture & Food Forest
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how nature sustains itself through intelligent design and natural systems
          </p>
        </div>

        {/* Permaculture Principles */}
        <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {principles.map((principle, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center"
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center ${principle.color}`}>
                <i className={`${principle.icon} text-2xl`}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{principle.title}</h3>
              <p className="text-gray-600 text-sm">{principle.description}</p>
            </div>
          ))}
        </div>

        {/* Permaculture Zones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {permacultureZones.map((zone, index) => (
            <div 
              key={index}
              className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
              style={{ transitionDelay: `${400 + index * 150}ms` }}
            >
              <div className="relative h-64 overflow-hidden">
                <img 
                  src={zone.image}
                  alt={zone.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {zone.zone}
                </div>
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${zone.icon} text-lg`}></i>
                  </div>
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{zone.title}</h3>
                <p className="text-gray-600 leading-relaxed">{zone.description}</p>
                <div className="mt-6 flex items-center text-green-600">
                  <div className="w-6 h-6 flex items-center justify-center mr-2">
                    <i className="ri-ecology-line text-lg"></i>
                  </div>
                  <span className="font-medium">Sustainable System</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Nature's Wisdom Section */}
        <div className={`bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-8 md:p-12 text-white transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <i className="ri-earth-line text-3xl"></i>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              How Nature Sustains Itself
            </h3>
            <p className="text-xl mb-8 text-green-100 leading-relaxed">
              In our permaculture systems, every element serves multiple functions, waste becomes resource, 
              and diversity creates stability. Experience the wisdom of natural ecosystems where everything 
              is connected and nothing is wasted.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="ri-recycle-line text-3xl mb-4 block"></i>
                <h4 className="text-lg font-semibold mb-2">Zero Waste</h4>
                <p className="text-green-100 text-sm">Every output becomes input for another system</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="ri-community-line text-3xl mb-4 block"></i>
                <h4 className="text-lg font-semibold mb-2">Mutual Support</h4>
                <p className="text-green-100 text-sm">Plants and animals help each other thrive</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="ri-time-line text-3xl mb-4 block"></i>
                <h4 className="text-lg font-semibold mb-2">Long-term Thinking</h4>
                <p className="text-green-100 text-sm">Designing for generations, not just seasons</p>
              </div>
            </div>

            <button className="bg-white text-green-600 hover:bg-green-50 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
              Explore Permaculture Tours
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Permaculture;